import { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Globe, Loader, Check, AlertCircle } from 'lucide-react'
import { useApi, asArray } from '../../hooks/useApi'
import { apiPost } from '../../lib/auth'
import ServiceConnectionCard, { type ServiceKind, type ServiceStatus } from './ServiceConnectionCard'
import ResourcePickerModal from './ResourcePickerModal'
import CalculatedFieldsManager from '../../features/calculated-fields/CalculatedFieldsManager'

// --- Types ---

interface ConnectionEntry {
  service: ServiceKind
  status: ServiceStatus
  accountEmail?: string | null
  config?: Record<string, unknown>
}

const SERVICES: ServiceKind[] = ['ga4', 'gsc', 'ads', 'gbp']

// --- Sub-components ---

function FeedbackMessage({ type, message }: { type: 'success' | 'error'; message: string }) {
  if (type === 'success') {
    return (
      <div className="flex items-center gap-2 text-xs text-emerald-600 mt-3">
        <Check className="w-3.5 h-3.5" strokeWidth={1.25} />
        {message}
      </div>
    )
  }
  return (
    <div className="flex items-center gap-2 text-xs text-red-500 mt-3">
      <AlertCircle className="w-3.5 h-3.5" strokeWidth={1.25} />
      {message}
    </div>
  )
}

const SERVICE_LABELS: Record<ServiceKind, string> = {
  ga4: 'Google Analytics 4',
  gsc: 'Search Console',
  ads: 'Google Ads',
  gbp: "Fiche d'établissement (GBP)",
}

// --- Main component ---

export default function DataSources() {
  const { projectId } = useParams<{ projectId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: connectionsData, loading: connectionsLoading, refetch: refetchConnections } = useApi<ConnectionEntry[]>(
    `/api/data-sources/clients/${projectId}/connections`
  )
  const connections = asArray(connectionsData)

  const { data: gbpLocations } = useApi<{ accessErrorCode?: string | null }[]>(
    `/api/local/clients/${projectId}/locations`
  )
  const notManagedCount = asArray(gbpLocations).filter((l) => l.accessErrorCode === 'NOT_MANAGER').length

  // ResourcePickerModal ouvert (retour callback OAuth)
  const [picker, setPicker] = useState<{ service: ServiceKind; googleAccountId: number; grant?: string } | null>(null)

  const [connectError, setConnectError] = useState<{ service: ServiceKind; message: string } | null>(null)
  const [disconnecting, setDisconnecting] = useState<ServiceKind | null>(null)

  // Bing (hors périmètre Google, inchangé)
  const [bingApiKey, setBingApiKey] = useState('')
  const [bingSiteUrl, setBingSiteUrl] = useState('')
  const [bingSaving, setBingSaving] = useState(false)
  const [bingFeedback, setBingFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const bingConnected = false

  // Retour du callback OAuth : ?connected=<service>&accountId=<id>&grant=<token>
  useEffect(() => {
    const connectedService = searchParams.get('connected') as ServiceKind | null
    const accountId = searchParams.get('accountId')
    const grant = searchParams.get('grant')
    if (connectedService && SERVICES.includes(connectedService) && accountId) {
      setPicker({ service: connectedService, googleAccountId: Number(accountId), grant: grant ?? undefined })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function clearCallbackParams() {
    const next = new URLSearchParams(searchParams)
    next.delete('connected')
    next.delete('accountId')
    next.delete('grant')
    setSearchParams(next, { replace: true })
  }

  async function redirectToAuthUrl(service: ServiceKind) {
    setConnectError(null)
    try {
      const res = await fetch(`/api/data-sources/google/auth-url?clientId=${projectId}&service=${service}`, {
        credentials: 'include',
      })
      const json = await res.json()
      if (json.authUrl) {
        window.location.href = json.authUrl
      } else {
        setConnectError({ service, message: json.error ?? "Impossible de générer l'URL de connexion" })
      }
    } catch {
      setConnectError({ service, message: "Impossible de générer l'URL de connexion" })
    }
  }

  async function handleDisconnect(service: ServiceKind) {
    setConnectError(null)
    setDisconnecting(service)
    try {
      const res = await fetch(`/api/data-sources/clients/${projectId}/connections/${service}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) {
        const json = await res.json().catch(() => ({}))
        setConnectError({ service, message: json.error ?? 'Impossible de déconnecter cette source' })
        return
      }
      await refetchConnections()
    } catch {
      setConnectError({ service, message: 'Impossible de déconnecter cette source' })
    } finally {
      setDisconnecting(null)
    }
  }

  function handlePickerDone() {
    setPicker(null)
    clearCallbackParams()
    refetchConnections()
  }

  function handlePickerCancel() {
    setPicker(null)
    clearCallbackParams()
  }

  async function connectBing() {
    if (!bingApiKey.trim() || !bingSiteUrl.trim()) return
    setBingSaving(true)
    setBingFeedback(null)
    const { error } = await apiPost(`/api/data-sources/clients/${projectId}/connect`, {
      bingApiKey: bingApiKey.trim(),
      bingSiteUrl: bingSiteUrl.trim(),
    })
    setBingSaving(false)
    if (error) {
      setBingFeedback({ type: 'error', message: error })
    } else {
      setBingFeedback({ type: 'success', message: 'Connecte avec succes' })
      setTimeout(() => setBingFeedback(null), 4000)
    }
  }

  // --- Render ---

  if (connectionsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="w-6 h-6 animate-spin text-[#8B8FA7]" strokeWidth={1.25} />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* ── Cartes de connexion par service Google ── */}
      {SERVICES.map((service) => {
        const entry = connections.find((c) => c.service === service)
        const status: ServiceStatus = entry?.status ?? 'not_connected'
        return (
          <div key={service}>
            <ServiceConnectionCard
              service={service}
              status={status}
              accountEmail={entry?.accountEmail}
              onConnect={() => redirectToAuthUrl(service)}
              onAuthorize={() => redirectToAuthUrl(service)}
              onReconnect={() => redirectToAuthUrl(service)}
              onChangeAccount={() => redirectToAuthUrl(service)}
              onDisconnect={() => handleDisconnect(service)}
            />
            {disconnecting === service && (
              <div className="flex items-center gap-2 text-xs text-[#8B8FA7] mt-2">
                <Loader className="w-3.5 h-3.5 animate-spin" strokeWidth={1.25} />
                Déconnexion...
              </div>
            )}
            {connectError?.service === service && (
              <FeedbackMessage type="error" message={connectError.message} />
            )}
            {service === 'gbp' && notManagedCount > 0 && (
              <p className="text-xs text-amber-600 mt-2">
                {notManagedCount} fiche{notManagedCount > 1 ? 's' : ''} non gérée{notManagedCount > 1 ? 's' : ''} par ce compte.
              </p>
            )}
          </div>
        )
      })}

      {/* ── Bing Webmaster Tools (hors périmètre Google) ── */}
      <div className="bg-white rounded-xl border border-[#E2E4ED] p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#6366F1]/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-[#6366F1]" strokeWidth={1.25} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#1B2559]">Bing Webmaster Tools</h3>
              <p className="text-xs text-[#8B8FA7] mt-0.5">Donnees de recherche Bing et performances</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-[#8B8FA7] rounded-full text-xs font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-[#8B8FA7]" />
            {bingConnected ? 'Connecte' : 'Non connecte'}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[#555A73] mb-1.5">API Key</label>
            <input
              type="password"
              value={bingApiKey}
              onChange={e => setBingApiKey(e.target.value)}
              placeholder="Votre cle API Bing"
              className="w-full px-3 py-2.5 bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl text-sm text-[#1B2559] placeholder-[#8B8FA7] focus:outline-none focus:border-[#6366F1] transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#555A73] mb-1.5">Site URL</label>
            <input
              type="url"
              value={bingSiteUrl}
              onChange={e => setBingSiteUrl(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-3 py-2.5 bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl text-sm text-[#1B2559] placeholder-[#8B8FA7] focus:outline-none focus:border-[#6366F1] transition-colors"
            />
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={connectBing}
            disabled={!bingApiKey.trim() || !bingSiteUrl.trim() || bingSaving}
            className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {bingSaving && <Loader className="w-4 h-4 animate-spin" strokeWidth={1.25} />}
            Sauvegarder
          </button>
        </div>
        {bingFeedback && <FeedbackMessage type={bingFeedback.type} message={bingFeedback.message} />}
      </div>

      {/* ── Champs calculés ── */}
      {projectId && (
        <div className="bg-white rounded-xl border border-[#E2E4ED] p-5">
          <h3 className="text-sm font-semibold text-[#1B2559] mb-1">Champs calculés</h3>
          <p className="text-xs text-[#8B8FA7] mb-4">Dimensions et métriques dérivées, réutilisables dans les rapports.</p>
          <CalculatedFieldsManager projectId={Number(projectId)} />
        </div>
      )}

      {/* ── ResourcePickerModal (retour callback OAuth) ── */}
      {picker && projectId && (
        <ResourcePickerModal
          clientId={projectId}
          service={picker.service}
          googleAccountId={picker.googleAccountId}
          grant={picker.grant}
          onDone={handlePickerDone}
          onCancel={handlePickerCancel}
        />
      )}
    </div>
  )
}
